import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  FilePenLine,
  Film,
  Image as ImageIcon,
  X,
} from "lucide-react";

import Pagination from "components/project/Pagination";
import Spinner from "components/Spinner";
import { FILE_LIST_COLUMNS, MIN_FILE_LIST_COLUMNS, readFileListColumns } from "lib/utils/fileListColumns";
import "css/fileListColumns.css";
import ProjectFileListControls from "./ProjectFileListControls";
import DetailConditionFilterDropdown from "components/project/DetailConditionFilterDropdown";
import { buildProjectDetailFilterGroups } from "components/project/projectDetailFilterGroups";
import { getFileList } from "lib/api/getfileList";
import { postMarkAsDeleted } from "lib/api/postMarkAsDeleted";
import { postUpdateFileMetadata } from "lib/api/postUpdateFileMetadata";
import { useCheckBoxGroup } from "lib/hooks/useCheckBoxGroup";
import { useAppStore } from "lib/store/useAppStore";
import {
  FILE_SORT_PREF,
  getFileSortPref,
  setFileSortPref,
} from "lib/utils/fileSortpref";
import {
  FILE_LIST_VIEW_PREF,
  getFileListViewPref,
  setFileListViewPref,
} from "lib/utils/fileListViewPref";
import {
  FILE_DETAIL_FILTER_QUERY_KEYS,
  countFileDetailFilters,
  normalizeFileDetailFilters,
  serializeFileDetailFilterValues,
} from "lib/utils/fileDetailFilters";
import { ProjectFileUploadPanel } from "../ProjectFileUploadPage.jsx";
import { buildProjectTreeTitleSegments } from "./projectOptionalTitleUtils";

const STATUS_ALIAS_TO_KEY = {
  0: "normal",
  1: "damaged",
  2: "pending",
  normal: "normal",
  ok: "normal",
  "\uC815\uC0C1": "normal",
  "\u6B63\u5E38": "normal",
  damaged: "damaged",
  "\uC190\uC0C1\uB428": "damaged",
  "\u7834\u640D": "damaged",
  pending: "pending",
  waiting: "pending",
  standby: "pending",
  "\uB300\uAE30": "pending",
  "\u5F85\u6A5F": "pending",
};

const FILE_TYPE_ALIAS_TO_KEY = {
  image: "image",
  img: "image",
  "\uC774\uBBF8\uC9C0": "image",
  "\u753B\u50CF": "image",
  video: "video",
  movie: "video",
  "\uB3D9\uC601\uC0C1": "video",
  "\u52D5\u753B": "video",
};

const FACILITY_TYPE_ALIAS_TO_KEY = {
  elec: "electric",
  electric: "electric",
  electrical: "electric",
  "\uC804\uAE30\uC124\uBE44": "electric",
  "\uC804\uAE30 \uC124\uBE44": "electric",
  "\u96FB\u6C17\u8A2D\u5099": "electric",
  gas: "gas",
  "\uAC00\uC2A4\uC124\uBE44": "gas",
  "\uAC00\uC2A4 \uC124\uBE44": "gas",
  "\u30AC\u30B9\u8A2D\u5099": "gas",
  machine: "machine",
  mechanical: "machine",
  "\uAE30\uACC4\uC124\uBE44": "machine",
  "\uAE30\uACC4 \uC124\uBE44": "machine",
  "\u6A5F\u68B0\u8A2D\u5099": "machine",

  电气: "electric",
  电气设备: "electric",
  燃气: "gas",
  燃气设备: "gas",
  机械: "machine",
  机械设备: "machine",

  "Thiết bị điện": "electric",
  "Thiết bị khí": "gas",
  "Thiết bị cơ khí": "machine",
};

const STATUS_CLASS_MAP = {
  normal: "status-ok",
  damaged: "status-damaged",
  pending: "status-wait",
};

const FILE_TYPE_CLASS_MAP = {
  image: "type-image",
  video: "type-video",
  doc: "type-doc",
};

const FACILITY_CLASS_MAP = {
  electric: "facility-elec",
  gas: "facility-gas",
  machine: "facility-machine",
};

const SEARCH_CATEGORY_KEYS = [
  {
    key: "file_name",
    labelKey: "projectDetail.fileList.search.categories.fileName",
  },
  {
    key: "facility_type",
    labelKey: "projectDetail.fileList.search.categories.facilityType",
  },
  {
    key: "location",
    labelKey: "projectDetail.fileList.search.categories.location",
  },
  {
    key: "facility_name",
    labelKey: "projectDetail.fileList.search.categories.facilityName",
  },
];

const DEFAULT_SEARCH_CATEGORY_KEY = "file_name";
const SEARCH_CATEGORY_KEY_SET = new Set(
  SEARCH_CATEGORY_KEYS.map((item) => item.key),
);

const FILE_METADATA_EDIT_FIELDS = [
  {
    key: "facility_type",
    labelKey: "projectDetail.fileList.column.facilityType",
    defaultLabel: "\uC124\uBE44 \uAD6C\uBD84",
  },
  {
    key: "building_name",
    labelKey: "projectDetail.fileList.column.buildingName",
    defaultLabel: "\uAC74\uBB3C\uBA85",
    maxLength: 100,
  },
  {
    key: "floor",
    labelKey: "projectDetail.fileList.column.floor",
    defaultLabel: "\uCE35",
    maxLength: 100,
  },
  {
    key: "location",
    labelKey: "projectDetail.fileList.column.location",
    defaultLabel: "\uC704\uCE58",
  },
  {
    key: "facility_name",
    labelKey: "projectDetail.fileList.column.facilityName",
    defaultLabel: "\uC124\uBE44\uBA85",
  },
  {
    key: "part_name",
    labelKey: "imageFileIndex.report.partName",
    defaultLabel: "\uBD80\uD488\uBA85",
    maxLength: 255,
  },
];

function getSearchCategoryKey(value) {
  return SEARCH_CATEGORY_KEY_SET.has(value)
    ? value
    : DEFAULT_SEARCH_CATEGORY_KEY;
}

function normalizeMetadataValue(value) {
  return String(value ?? "").trim();
}

function getFileMetadataInitialValues(item = {}) {
  return Object.fromEntries(
    FILE_METADATA_EDIT_FIELDS.map((field) => [
      field.key,
      normalizeMetadataValue(item?.[field.key]),
    ]),
  );
}

function getBulkMetadataInitialValues(items = []) {
  return Object.fromEntries(
    FILE_METADATA_EDIT_FIELDS.map((field) => {
      const values = items.map((item) =>
        normalizeMetadataValue(item?.[field.key]),
      );
      const firstValue = values[0] ?? "";
      const hasSameValue = values.every((value) => value === firstValue);
      return [field.key, hasSameValue ? firstValue : ""];
    }),
  );
}

function getMetadataFieldLabel(field, t) {
  return t(field.labelKey, {
    defaultValue: field.defaultLabel,
  });
}

function normalizeAliasValue(value, aliasMap, fallback) {
  if (value == null) return fallback;
  const raw = String(value).trim();
  if (!raw) return fallback;
  return aliasMap[raw] || aliasMap[raw.toLowerCase()] || fallback;
}

function getStatusKey(status) {
  return normalizeAliasValue(status, STATUS_ALIAS_TO_KEY, "normal");
}

function getFileTypeKey(type) {
  return normalizeAliasValue(type, FILE_TYPE_ALIAS_TO_KEY, "doc");
}

function getFacilityKey(type) {
  return normalizeAliasValue(type, FACILITY_TYPE_ALIAS_TO_KEY, null);
}

function toPreviewSrc(source, mimeType) {
  if (typeof source !== "string") return "";
  const trimmed = source.trim();
  if (!trimmed) return "";
  if (/^(data:|https?:|blob:|\/)/i.test(trimmed)) return trimmed;
  if (trimmed.includes("base64,")) return trimmed;
  if (trimmed.length > 50) return `data:${mimeType};base64,${trimmed}`;
  return trimmed;
}

function getPendingPreviewSrc() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="960" height="720" viewBox="0 0 960 720">
      <defs>
        <linearGradient id="pendingBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#fff7ed"/>
          <stop offset="1" stop-color="#ffedd5"/>
        </linearGradient>
      </defs>
      <rect width="960" height="720" rx="36" fill="url(#pendingBg)"/>
      <rect x="92" y="88" width="776" height="544" rx="28" fill="#ffffff" stroke="#fdba74" stroke-width="8"/>
      <circle cx="480" cy="272" r="88" fill="#fed7aa"/>
      <path d="M480 218v62" stroke="#c2410c" stroke-width="26" stroke-linecap="round"/>
      <circle cx="480" cy="338" r="14" fill="#c2410c"/>
      <text x="480" y="472" text-anchor="middle" fill="#9a3412" font-family="Arial" font-size="52" font-weight="700">PENDING</text>
      <text x="480" y="536" text-anchor="middle" fill="#c2410c" font-family="Arial" font-size="30" font-weight="600">Preview will be available after processing</text>
    </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function getPreviewSrc(item) {
  const statusKey = getStatusKey(item?.is_corrupted);
  const typeKey = getFileTypeKey(item?.file_type);
  if (statusKey === "pending") {
    return getPendingPreviewSrc();
  }
  if (typeKey === "image") {
    return (
      toPreviewSrc(item?.image_visual, "image/jpeg") ||
      toPreviewSrc(item?.image_thumbnail, "image/jpeg") ||
      "/images/placeholder_image.png"
    );
  }
  if (typeKey === "video") {
    return (
      toPreviewSrc(item?.frame_image_1, "image/png") ||
      toPreviewSrc(item?.image_visual, "image/jpeg") ||
      "/images/placeholder_image.png"
    );
  }
  return "/images/placeholder_image.png";
}

function getMetadataPreviewSrc(item) {
  const statusKey = getStatusKey(item?.is_corrupted);
  const typeKey = getFileTypeKey(item?.file_type);
  if (statusKey === "pending") {
    return getPendingPreviewSrc();
  }
  if (typeKey === "image") {
    return (
      toPreviewSrc(item?.image_visual, "image/jpeg") ||
      "/images/placeholder_image.png"
    );
  }
  if (typeKey === "video") {
    return (
      toPreviewSrc(item?.frame_image_1, "image/png") ||
      "/images/placeholder_image.png"
    );
  }
  return "/images/placeholder_image.png";
}

function createSingleMetadataEditTarget(item, slideIndex, slideCount) {
  return {
    mode: "single",
    ids: [item.id],
    count: 1,
    fileName: item?.file_name ?? "",
    previewSrc: getMetadataPreviewSrc(item),
    previewAlt: item?.file_name ?? "",
    initialValues: getFileMetadataInitialValues(item),
    slideIndex,
    slideCount,
  };
}

function getVideoSrc(item) {
  const statusKey = getStatusKey(item?.is_corrupted);
  const typeKey = getFileTypeKey(item?.file_type);
  if (statusKey === "pending" || typeKey !== "video") return "";
  return toPreviewSrc(item?.video_mp4, "video/mp4");
}

function getDetailMeta(projectId, item, detailSearch = "") {
  const statusKey = getStatusKey(item?.is_corrupted);
  const fileTypeKey = getFileTypeKey(item?.file_type);
  const facilityValue = String(item?.facility_type ?? "").trim();
  const facilityKey = facilityValue
    ? getFacilityKey(item?.facility_type)
    : null;
  const statusClass = STATUS_CLASS_MAP[statusKey] ?? "status-ok";
  const fileTypeClass = FILE_TYPE_CLASS_MAP[fileTypeKey] ?? "type-doc";
  const facilityClass = facilityKey
    ? (FACILITY_CLASS_MAP[facilityKey] ?? "")
    : "";
  const canOpenDetail =
    statusKey !== "pending" &&
    (fileTypeKey === "image" || fileTypeKey === "video");
  const fileRoute = fileTypeKey === "video" ? "video" : "image";

  return {
    statusKey,
    fileTypeKey,
    facilityKey,
    statusClass,
    fileTypeClass,
    facilityClass,
    canOpenDetail,
    detailPath: canOpenDetail
      ? `/project/${projectId}/file_${fileRoute}/${item?.id}${detailSearch}`
      : null,
    previewSrc: getPreviewSrc(item),
    videoSrc: getVideoSrc(item),
  };
}

export default function ProjectFileListSection({
  user_id,
  project_id,
  project_name,
  fetchProjectTree,
  onProjectMutation,
  page,
  limit = 20,
  category,
  index,
  value,
  treeBuildingName,
  treeFloor,
  treeLocation,
  treeFacilityType,
  treeFacilityName,
  searchCategory,
  searchKeyword = "",
  sp,
  setSp,
  t,
  i18n,
  detailFilters = {},
}) {
  const [prefReady, setPrefReady] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => readFileListColumns(user_id));
  useEffect(() => { setVisibleColumns(readFileListColumns(user_id)); }, [user_id]);
  const toggleColumn = (key, checked) => {
    const next = checked ? [...visibleColumns, key] : visibleColumns.filter(item => item !== key);
    if (next.length < MIN_FILE_LIST_COLUMNS) return;
    setVisibleColumns(next);
    try { if (user_id) localStorage.setItem(`file_list_columns_v1:${user_id}`, JSON.stringify(next)); } catch { /* Keep the current selection. */ }
  };
  const treePartName = sp.get("treePartName");
  const treeNodeId = sp.get("treeNodeId");
  const [viewMode, setViewMode] = useState(
    FILE_LIST_VIEW_PREF.DEFAULT_VIEW_MODE,
  );
  const reqSeq = useRef(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
  const [metadataEditTarget, setMetadataEditTarget] = useState(null);
  const [isMetadataSaving, setIsMetadataSaving] = useState(false);

  const [currentSortPref, setCurrentSortPref] = useState(
    () => FILE_SORT_PREF.DEFAULT_SORT,
  );
  const currentDetailFilters = useMemo(
    () => normalizeFileDetailFilters(detailFilters),
    [detailFilters],
  );
  const currentSearchCategory = useMemo(
    () => getSearchCategoryKey(searchCategory),
    [searchCategory],
  );
  const currentSearchKeyword = useMemo(
    () => String(searchKeyword ?? "").trim(),
    [searchKeyword],
  );
  const roadmapUnassignedLabel = t("projectDetail.roadmap.unassigned", {
    defaultValue: "미지정",
  });
  const roadmapScopeSegments = useMemo(
    () => buildProjectTreeTitleSegments(sp, roadmapUnassignedLabel),
    [roadmapUnassignedLabel, sp],
  );
  const roadmapScopeLabel = useMemo(
    () => roadmapScopeSegments.map((segment) => segment.label).join(" / "),
    [roadmapScopeSegments],
  );
  const legacyScopeLabel = ["location", "facility_type", "facility_name"].includes(category) &&
    typeof index === "string" && index !== "undefined"
    ? [index, category !== "facility_name" && value !== "undefined" ? value : null]
        .filter((item) => item != null)
        .map((item) => item || roadmapUnassignedLabel)
        .join(" / ")
    : "";
  const fileListScopeLabel = [roadmapScopeLabel, legacyScopeLabel].filter(Boolean).join(" · ") ||
    (treeNodeId ? t("projectDetail.fileList.controls.selectedPath", { defaultValue: "Selected path" }) : "");

  const showAlert = useAppStore((s) => s.showAlert);

  const {
    checkedItems,
    setCheckedItems,
    isAllChecked,
    handleToggleItem,
    handleToggleAll,
  } = useCheckBoxGroup(rawData);

  useEffect(() => {
    setCheckedItems([]);
  }, [
    category,
    currentDetailFilters,
    currentSearchCategory,
    currentSearchKeyword,
    index,
    limit,
    page,
    project_id,
    setCheckedItems,
    treePartName,
    treeNodeId,
    treeBuildingName,
    treeFacilityName,
    treeFacilityType,
    treeFloor,
    treeLocation,
    value,
  ]);

  const selectedMetadataItems = useMemo(
    () => sortedData.filter((item) => checkedItems.includes(item?.id)),
    [checkedItems, sortedData],
  );

  useEffect(() => {
    if (!user_id) return;
    setPrefReady(false);
    const pref = getFileSortPref(user_id) ?? FILE_SORT_PREF.DEFAULT_SORT;
    setCurrentSortPref(pref);
    setViewMode(getFileListViewPref(user_id));
    setPrefReady(true);
  }, [user_id]);

  const applyViewMode = useCallback(
    (nextViewMode) => {
      setViewMode(nextViewMode);
      setFileListViewPref(user_id, nextViewMode);
    },
    [user_id],
  );

  const fetchFileList = useCallback(async () => {
    const seq = ++reqSeq.current;

    try {
      setLoading(true);
      setError(null);

      const { sortKey, sortDir } = currentSortPref;
      const response = await getFileList(project_id, {
        sortKey,
        sortDir,
        page,
        limit,
        category,
        index,
        value,
        treeBuildingName,
        treeFloor,
        treeLocation,
        treeFacilityType,
        treeFacilityName,
        treePartName,
        treeNodeId,
        searchCategory: currentSearchCategory,
        searchKeyword: currentSearchKeyword,
        fileType: currentDetailFilters.fileType,
        facilityType: currentDetailFilters.facilityType,
        fusionResult: currentDetailFilters.fusionResult,
        include_visual: 1,
      });

      if (seq !== reqSeq.current) return;

      if (!response.success) {
        setError(
          response.message ||
            t("projectDetail.fileList.errorFetchFailed", {
              defaultValue: "Failed to load file list.",
            }),
        );
        setRawData([]);
        setSortedData([]);
        setTotalCount(0);
        return;
      }

      const files = Array.isArray(response.files) ? response.files : [];
      setRawData(files);
      setSortedData(files);
      setTotalCount(response.pagination?.totalCount ?? 0);
    } catch (fetchError) {
      if (seq !== reqSeq.current) return;
      console.error("[FileList] getFileList failed:", fetchError);
      setError(
        t("projectDetail.fileList.errorServerRequest", {
          defaultValue: "An error occurred while requesting the server.",
        }),
      );
      setRawData([]);
      setSortedData([]);
    } finally {
      if (seq === reqSeq.current) {
        setLoading(false);
      }
    }
  }, [
    currentSortPref,
    currentDetailFilters,
    project_id,
    page,
    limit,
    category,
    index,
    value,
    treeBuildingName,
    treeLocation,
    treeFacilityType,
    treeFacilityName,
    treeFloor,
    treePartName,
    treeNodeId,
    currentSearchCategory,
    currentSearchKeyword,
    t,
  ]);

  useEffect(() => {
    if (!user_id || !project_id || !prefReady) return;
    fetchFileList();
  }, [user_id, project_id, prefReady, fetchFileList]);

  const handleDeleteFile = async () => {
    if (checkedItems.length === 0) {
      showAlert({
        text: t("projectDetail.fileList.selectFileToDelete", {
          defaultValue: "Please select files to delete.",
        }),
        success: false,
        autoClose: true,
      });
      return;
    }

    const { message, messageKey, messageParams, success } =
      await postMarkAsDeleted(project_id, checkedItems);
    setCheckedItems([]);
    showAlert({
      text: message,
      messageKey,
      messageParams,
      success,
      autoClose: true,
    });

    if (success) {
      await Promise.allSettled([
        fetchFileList(),
        fetchProjectTree?.(user_id, project_id),
        onProjectMutation?.({
          reason: "file-delete",
        }),
      ]);
      return;
    }

    await fetchFileList();
  };

  const openBulkMetadataEdit = useCallback(() => {
    if (selectedMetadataItems.length < 1) {
      showAlert({
        text: t("projectDetail.fileList.metadataEdit.selectFiles", {
          defaultValue:
            "\uC218\uC815\uD560 \uD30C\uC77C\uC744 \uC120\uD0DD\uD558\uC138\uC694.",
        }),
        success: false,
        autoClose: true,
      });
      return;
    }

    setMetadataEditTarget({
      mode: "bulk",
      ids: selectedMetadataItems.map((item) => item.id),
      count: selectedMetadataItems.length,
      items: selectedMetadataItems.map((item) => ({
        id: item.id,
        facility_type: item?.facility_type,
        building_name: item?.building_name,
        floor: item?.floor,
        location: item?.location,
        facility_name: item?.facility_name,
        part_name: item?.part_name,
      })),
      initialValues: getBulkMetadataInitialValues(selectedMetadataItems),
    });
  }, [selectedMetadataItems, showAlert, t]);

  const openSingleMetadataEdit = useCallback(
    (item) => {
      if (!item?.id) return;
      const slideIndex = sortedData.findIndex(
        (candidate) => candidate?.id === item.id,
      );
      setMetadataEditTarget(
        createSingleMetadataEditTarget(
          item,
          Math.max(slideIndex, 0),
          sortedData.length,
        ),
      );
    },
    [sortedData],
  );

  const navigateSingleMetadataEdit = useCallback(
    (direction) => {
      if (isMetadataSaving) return;

      setMetadataEditTarget((current) => {
        if (current?.mode !== "single") return current;

        const currentId = current.ids?.[0];
        const currentIndex = sortedData.findIndex(
          (item) => item?.id === currentId,
        );
        const nextIndex = currentIndex + direction;
        const nextItem = sortedData[nextIndex];

        if (!nextItem?.id) return current;
        return createSingleMetadataEditTarget(
          nextItem,
          nextIndex,
          sortedData.length,
        );
      });
    },
    [isMetadataSaving, sortedData],
  );

  const closeMetadataEdit = useCallback(() => {
    if (isMetadataSaving) return;
    setMetadataEditTarget(null);
  }, [isMetadataSaving]);

  const handleSubmitMetadataEdit = useCallback(
    async (fields, options = {}) => {
      if (!metadataEditTarget || isMetadataSaving) return;

      const safeFields =
        fields && typeof fields === "object" && !Array.isArray(fields)
          ? fields
          : {};

      if (Object.keys(safeFields).length < 1) {
        showAlert({
          text: t("projectDetail.fileList.metadataEdit.noChanges", {
            defaultValue:
              "\uBCC0\uACBD\uD560 \uB0B4\uC6A9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
          }),
          success: false,
          autoClose: true,
        });
        return;
      }

      setIsMetadataSaving(true);
      try {
        const isEmptyOnly =
          metadataEditTarget.mode === "bulk" &&
          options?.applyMode === "emptyOnly";
        let response;

        if (isEmptyOnly) {
          const targetItems = Array.isArray(metadataEditTarget.items)
            ? metadataEditTarget.items
            : [];
          const requestPlans = FILE_METADATA_EDIT_FIELDS.flatMap((field) => {
            const value = normalizeMetadataValue(safeFields[field.key]);
            if (!value) return [];

            const emptyIds = targetItems
              .filter((item) => !normalizeMetadataValue(item?.[field.key]))
              .map((item) => item.id)
              .filter(Boolean);

            return emptyIds.length > 0
              ? [
                  {
                    ids: emptyIds,
                    fields: {
                      [field.key]: value,
                    },
                  },
                ]
              : [];
          });

          if (requestPlans.length < 1) {
            showAlert({
              text: t("projectDetail.fileList.metadataEdit.noEmptyFields", {
                defaultValue:
                  "\uC801\uC6A9\uD560 \uBE48\uCE78\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
              }),
              success: false,
              autoClose: true,
            });
            return;
          }

          const results = await Promise.all(
            requestPlans.map((plan) =>
              postUpdateFileMetadata(project_id, plan.ids, plan.fields),
            ),
          );
          const failedResult = results.find((result) => !result.success);
          const updatedCount = results.reduce(
            (sum, result) => sum + Number(result?.data?.updated ?? 0),
            0,
          );

          response = failedResult || {
            success: true,
            message: t("projectDetail.fileList.metadataEdit.fillEmptySuccess", {
              defaultValue:
                "\uBE48\uCE78 {{count}}\uAC74\uC744 \uC218\uC815\uD588\uC2B5\uB2C8\uB2E4.",
              count: updatedCount,
            }),
            messageKey: "",
            messageParams: {},
          };
        } else {
          response = await postUpdateFileMetadata(
            project_id,
            metadataEditTarget.ids,
            safeFields,
          );
        }

        showAlert({
          text:
            response.message ||
            t(
              response.success
                ? "projectDetail.fileList.metadataEdit.success"
                : "projectDetail.fileList.metadataEdit.failed",
              {
                defaultValue: response.success
                  ? "\uD30C\uC77C \uC815\uBCF4\uB97C \uC218\uC815\uD588\uC2B5\uB2C8\uB2E4."
                  : "\uD30C\uC77C \uC815\uBCF4 \uC218\uC815\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
              },
            ),
          messageKey: response.messageKey,
          messageParams: response.messageParams,
          success: response.success,
          autoClose: true,
        });

        if (!response.success) return;

        setMetadataEditTarget(null);
        if (metadataEditTarget.mode === "bulk") {
          const updatedIdSet = new Set(metadataEditTarget.ids);
          setCheckedItems((current) =>
            current.filter((id) => !updatedIdSet.has(id)),
          );
        }

        await Promise.allSettled([
          fetchFileList(),
          fetchProjectTree?.(user_id, project_id),
          onProjectMutation?.({
            reason: "file-metadata-update",
          }),
        ]);
      } finally {
        setIsMetadataSaving(false);
      }
    },
    [
      fetchFileList,
      fetchProjectTree,
      isMetadataSaving,
      metadataEditTarget,
      onProjectMutation,
      project_id,
      setCheckedItems,
      showAlert,
      t,
      user_id,
    ],
  );

  const handleRefreshFileList = async () => {
    if (!user_id || !project_id || loading) return;

    await Promise.allSettled([
      fetchFileList(),
      fetchProjectTree?.(user_id, project_id),
      onProjectMutation?.({
        reason: "file-refresh",
      }),
    ]);
  };

  const handlePageChange = (nextPage) => {
    const nextParams = new URLSearchParams(sp);
    nextParams.set("page", String(nextPage));
    nextParams.set("limit", String(limit));
    setSp(nextParams, { replace: false });
  };

  const applySortPref = (nextPref) => {
    setCurrentSortPref(nextPref);
    setFileSortPref(user_id, nextPref);
    handlePageChange(1);
  };

  const applyDetailFilters = useCallback(
    (nextFilters) => {
      const normalizedFilters = normalizeFileDetailFilters(nextFilters);
      const nextParams = new URLSearchParams(sp);

      Object.entries(FILE_DETAIL_FILTER_QUERY_KEYS).forEach(
        ([filterKey, queryKey]) => {
          const serializedValue = serializeFileDetailFilterValues(
            normalizedFilters[filterKey],
          );

          if (serializedValue) {
            nextParams.set(queryKey, serializedValue);
          } else {
            nextParams.delete(queryKey);
          }
        },
      );

      nextParams.set("page", "1");
      nextParams.set("limit", String(limit));
      setSp(nextParams, { replace: false });
    },
    [limit, setSp, sp],
  );

  const clearDetailFilters = useCallback(() => {
    applyDetailFilters({});
  }, [applyDetailFilters]);

  const applyFileSearch = useCallback(
    ({ categoryKey, keyword }) => {
      const normalizedCategory = getSearchCategoryKey(categoryKey);
      const normalizedKeyword = String(keyword ?? "").trim();
      const previousKeyword = sp.get("searchKeyword") ?? "";
      const previousCategory = getSearchCategoryKey(sp.get("searchCategory"));

      if (
        normalizedKeyword === previousKeyword &&
        (!normalizedKeyword || normalizedCategory === previousCategory)
      ) {
        return;
      }

      const nextParams = new URLSearchParams(sp);

      if (normalizedKeyword) {
        nextParams.set("searchCategory", normalizedCategory);
        nextParams.set("searchKeyword", normalizedKeyword);
      } else {
        nextParams.delete("searchCategory");
        nextParams.delete("searchKeyword");
      }

      nextParams.set("page", "1");
      nextParams.set("limit", String(limit));
      setSp(nextParams, { replace: true });
    },
    [limit, setSp, sp],
  );

  const handleInlineUploadSuccess = useCallback(async () => {
    if (!user_id || !project_id) return;

    if (page !== 1) {
      const nextParams = new URLSearchParams(sp);
      nextParams.set("page", "1");
      nextParams.set("limit", String(limit));
      setSp(nextParams, { replace: false });
    }

    await Promise.allSettled([
      page === 1 ? fetchFileList() : Promise.resolve(),
      onProjectMutation?.({
        reason: "file-upload",
      }),
    ]);
  }, [
    fetchFileList,
    limit,
    onProjectMutation,
    page,
    project_id,
    setSp,
    sp,
    user_id,
  ]);

  const searchCategoryOptions = useMemo(
    () =>
      SEARCH_CATEGORY_KEYS.map((item) => ({
        key: item.key,
        label: t(item.labelKey),
      })),
    [t],
  );
  const activeDetailFilterCount = countFileDetailFilters(currentDetailFilters);
  const fileDetailSearch = useMemo(() => {
    const query = new URLSearchParams(sp).toString();
    return query ? `?${query}` : "";
  }, [sp]);
  const detailFilterGroups = useMemo(
    () => buildProjectDetailFilterGroups({ t, i18n }),
    [i18n, t],
  );
  const uploadToggleLabel = isUploadPanelOpen
    ? t("projectDetail.fileUpload.uploadedList.closePreview", {
        defaultValue: "Close",
      })
    : t("aside.fileUploadLink", {
        defaultValue: "File Upload",
      });
  const detailFilterDropdown = (
    <DetailConditionFilterDropdown
      label={t("projectDetail.fileList.detailFilter.trigger", {
        defaultValue: "Detailed filters",
      })}
      groups={detailFilterGroups}
      value={currentDetailFilters}
      onChange={applyDetailFilters}
      onClear={clearDetailFilters}
      activeCount={activeDetailFilterCount}
      activeText={t("projectDetail.fileList.detailFilter.activeCount", {
        defaultValue: "{{count}} active",
        count: activeDetailFilterCount,
      })}
      clearLabel={t("projectDetail.fileList.detailFilter.clear", {
        defaultValue: "Clear filters",
      })}
      applyLabel={t("projectDetail.fileList.detailFilter.apply", {
        defaultValue: "Apply",
      })}
    />
  );
  const visibleCheckedCount = sortedData.filter((item) =>
    checkedItems.includes(item?.id),
  ).length;
  return (
    <div className="fileList fileList--organized">
      <ProjectFileListControls
        t={t}
        loading={loading}
        error={error}
        totalCount={totalCount}
        scopeLabel={fileListScopeLabel}
        detailFilterDropdown={detailFilterDropdown}
        searchCategoryOptions={searchCategoryOptions}
        searchCategory={currentSearchCategory}
        searchKeyword={currentSearchKeyword}
        onSearchChange={applyFileSearch}
        sortPref={currentSortPref}
        onSortChange={applySortPref}
        onRefresh={handleRefreshFileList}
        viewMode={viewMode}
        onViewModeChange={applyViewMode}
        visibleColumns={visibleColumns}
        onToggleColumn={toggleColumn}
        isUploadPanelOpen={isUploadPanelOpen}
        uploadDisabled={!project_id}
        uploadToggleLabel={uploadToggleLabel}
        onToggleUpload={() => setIsUploadPanelOpen((current) => !current)}
        uploadPanel={
          <div id="project-file-upload-panel" className="fileListDetailFilterRow" hidden={!isUploadPanelOpen} style={!isUploadPanelOpen ? { display: "none" } : undefined}>
            <ProjectFileUploadPanel
              onFilesDrag={() => setIsUploadPanelOpen(true)}
              key={project_id}
              className="fileUpload--inline fileListDetailUploadPanel"
              projectId={project_id}
              projectName={project_name}
              userId={user_id}
              defaultMetadata={{
                buildingName: treeBuildingName,
                facilityType: treeFacilityType,
                floor: treeFloor,
                location: treeLocation,
                facilityName: treeFacilityName,
                partName: treePartName,
              }}
              fetchProjectTree={fetchProjectTree}
              showAlert={showAlert}
              onUploadSuccess={handleInlineUploadSuccess}
              showTitle={false}
            />
          </div>
        }
        selectionActions={visibleCheckedCount > 0 ? (
          <>
            <span className="fileList__selectionStatus">
              {t("projectDetail.fileList.selectionStatus", { count: visibleCheckedCount })}
            </span>
            <button type="button" onClick={openBulkMetadataEdit} disabled={loading} className="btn-bg-blue fileMetadataBulkButton">
              {t("projectDetail.fileList.metadataEdit.bulkButton", { defaultValue: "일괄 수정" })}
            </button>
            <button type="button" onClick={() => handleDeleteFile(checkedItems)} disabled={loading} className="fileListControls__delete">
              {t("projectDetail.fileList.deleteButton")}
            </button>
          </>
        ) : null}
      />

      {loading ? (
        <div className="fileListResultsState" aria-busy="true"><Spinner position="absolute" type="report" /></div>
      ) : error ? (
        <div className="fileListResultsState" role="alert">{error}</div>
      ) : viewMode === "table" ? (
        <div className="card detailTableWrap fileTableCard fileTableCard--customColumns" tabIndex={0} role="region" aria-label="파일 목록">
          <table className="detailTable fileKpiTable" style={{ width: "100%", minWidth: 50 + FILE_LIST_COLUMNS.reduce((sum, [key, , width]) => sum + (visibleColumns.includes(key) ? width : 0), 0) }}>
            <Column
              visibleColumns={visibleColumns}
              currentSortPref={currentSortPref}
              onSortChange={applySortPref}
              isAllChecked={isAllChecked}
              handleToggleAll={handleToggleAll}
              t={t}
            />
            <Raw
              visibleColumns={visibleColumns}
              project_id={project_id}
              data={sortedData}
              handleToggleItem={handleToggleItem}
              checkedItems={checkedItems}
              detailSearch={fileDetailSearch}
              onEditMetadata={openSingleMetadataEdit}
              t={t}
            />
          </table>
        </div>
      ) : (
        <FileCardGrid
          project_id={project_id}
          data={sortedData}
          checkedItems={checkedItems}
          handleToggleItem={handleToggleItem}
          detailSearch={fileDetailSearch}
          onEditMetadata={openSingleMetadataEdit}
          t={t}
        />
      )}

      {metadataEditTarget ? (
        <FileMetadataEditModal
          target={metadataEditTarget}
          t={t}
          isSaving={isMetadataSaving}
          onClose={closeMetadataEdit}
          onNavigate={navigateSingleMetadataEdit}
          onSubmit={handleSubmitMetadataEdit}
        />
      ) : null}

      {!loading && !error && <Pagination
        page={page}
        limit={limit}
        totalCount={totalCount}
        onPrev={() => handlePageChange(Math.max(1, page - 1))}
        onNext={() => handlePageChange(page + 1)}
        onPage={(nextPage) => handlePageChange(nextPage)}
      />}
    </div>
  );
}

function Column({
  visibleColumns = FILE_LIST_COLUMNS.map(([key]) => key),
  currentSortPref,
  onSortChange,
  isAllChecked,
  handleToggleAll,
  t,
}) {
  const handleCategoryClick = (sortKey) => {
    const isCurrentKey = currentSortPref?.sortKey === sortKey;
    const nextSortDir = isCurrentKey
      ? currentSortPref.sortDir === "ASC"
        ? "DESC"
        : "ASC"
      : sortKey === "uploaded_at"
        ? "DESC"
        : "ASC";

    onSortChange({ sortKey, sortDir: nextSortDir });
  };

  const renderSortableHeader = (sortKey, label) => {
    if (!visibleColumns.includes(sortKey)) return null;
    const isActive = currentSortPref?.sortKey === sortKey;
    const sortDir = isActive ? currentSortPref?.sortDir : null;

    return (
      <th style={{ minWidth: FILE_LIST_COLUMNS.find(([key]) => key === sortKey)?.[2] }}
        aria-sort={
          sortDir === "ASC"
            ? "ascending"
            : sortDir === "DESC"
              ? "descending"
              : "none"
        }
      >
        <button
          type="button"
          className={`fileListColumnSortButton ${isActive ? "isActive" : ""}`}
          onClick={() => handleCategoryClick(sortKey)}
          title={label}
        >
          <span>{label}</span>
          <span
            className="fileListColumnSortButton__indicator"
            aria-hidden="true"
          >
            {sortDir === "ASC" ? "↑" : sortDir === "DESC" ? "↓" : "↕"}
          </span>
        </button>
      </th>
    );
  };

  return (
    <thead>
      <tr>
        <th style={{ width: "50px" }}>
          <input
            checked={isAllChecked}
            onChange={handleToggleAll}
            id="allSelectCheckBox"
            type="checkbox"
          />
        </th>
        {renderSortableHeader(
          "uploaded_at",
          t("projectDetail.fileList.uploadDate"),
        )}
        {renderSortableHeader(
          "file_name",
          t("projectDetail.fileList.fileName"),
        )}
        {renderSortableHeader(
          "is_corrupted",
          t("projectDetail.fileList.column.fileStatus"),
        )}
        {renderSortableHeader(
          "file_type",
          t("projectDetail.fileList.column.fileType"),
        )}
        {renderSortableHeader(
          "building_name",
          t("projectDetail.fileList.column.buildingName"),
        )}
        {renderSortableHeader(
          "floor",
          t("projectDetail.fileList.column.floor"),
        )}
        {renderSortableHeader(
          "location",
          t("projectDetail.fileList.column.location"),
        )}
        {renderSortableHeader(
          "facility_type",
          t("projectDetail.fileList.column.facilityType"),
        )}
        {renderSortableHeader(
          "facility_name",
          t("projectDetail.fileList.column.facilityName"),
        )}
        {visibleColumns.includes("part_name") && <th style={{ minWidth: 160 }}>부품명</th>}
        <th hidden={!visibleColumns.includes("preview")}>
          {t("projectDetail.fileList.column.previewAndEdit", {
            defaultValue: "\uBBF8\uB9AC\uBCF4\uAE30 \uBC0F \uC218\uC815",
          })}
        </th>
        {/* <th>{t("projectDetail.fileList.column.is3DGenerated")}</th> */}
      </tr>
    </thead>
  );
}

function Raw({
  visibleColumns = FILE_LIST_COLUMNS.map(([key]) => key),
  project_id,
  data,
  checkedItems,
  handleToggleItem,
  detailSearch = "",
  onEditMetadata,
  t,
}) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <tbody>
        <tr className="emptyRow">
          <td colSpan={visibleColumns.length + 1} className="emptyCell">
            {t("projectDetail.fileList.emptyMessage", {
              defaultValue: "Please upload UD files.",
            })}
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {data.map((item) => {
        const {
          statusKey,
          fileTypeKey,
          statusClass,
          fileTypeClass,
          facilityClass,
          canOpenDetail,
          detailPath,
        } = getDetailMeta(project_id, item, detailSearch);

        return (
          <tr key={item?.id}>
            <td style={{ textAlign: "center" }}>
              <input
                checked={checkedItems.includes(item.id)}
                onChange={() => handleToggleItem(item.id)}
                type="checkbox"
              />
            </td>
            <td hidden={!visibleColumns.includes("uploaded_at")}>{item?.uploaded_at}</td>
            <td hidden={!visibleColumns.includes("file_name")}>
              {canOpenDetail ? (
                <Link className="linkStyle_Blue" to={detailPath}>
                  {item?.file_name}
                </Link>
              ) : (
                <span>{item?.file_name}</span>
              )}
            </td>
            <td hidden={!visibleColumns.includes("is_corrupted")}>
              <span className={`statusTag ${statusClass}`}>
                {t(`projectDetail.fileList.status.${statusKey}`, {
                  defaultValue: item?.is_corrupted ?? "",
                })}
              </span>
            </td>
            <td hidden={!visibleColumns.includes("file_type")}>
              <span className={`typeTag ${fileTypeClass}`}>
                {t(`projectDetail.fileList.fileType.${fileTypeKey}`, {
                  defaultValue: item?.file_type ?? "",
                })}
              </span>
            </td>
            <td hidden={!visibleColumns.includes("building_name")}>{item?.building_name}</td>
            <td hidden={!visibleColumns.includes("floor")}>{item?.floor}</td>
            <td hidden={!visibleColumns.includes("location")}>{item?.location}</td>
            <td hidden={!visibleColumns.includes("facility_type")}>
              <span
                className={facilityClass ? `facilityTag ${facilityClass}` : ""}
              >
                {item?.facility_type}
              </span>
            </td>
            <td hidden={!visibleColumns.includes("facility_name")}>{item?.facility_name}</td>
            <td hidden={!visibleColumns.includes("part_name")}>{item?.part_name}</td>
            <td className="filePreviewEditCell" hidden={!visibleColumns.includes("preview")}>
              <button
                type="button"
                onClick={() => onEditMetadata?.(item)}
                title={t("projectDetail.fileList.column.previewAndEdit", {
                  defaultValue: "\uBBF8\uB9AC\uBCF4\uAE30 \uBC0F \uC218\uC815",
                })}
                aria-label={t("projectDetail.fileList.column.previewAndEdit", {
                  defaultValue: "\uBBF8\uB9AC\uBCF4\uAE30 \uBC0F \uC218\uC815",
                })}
                className="fileMetadataIconButton"
              >
                <FilePenLine size={15} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </td>
            {/* <td style={{ textAlign: "center" }}>{item?.upload_status}</td> */}
          </tr>
        );
      })}
    </tbody>
  );
}

function FileCardGrid({
  project_id,
  data,
  checkedItems,
  handleToggleItem,
  detailSearch = "",
  onEditMetadata,
  t,
}) {
  const navigate = useNavigate();
  const hoverTimerRef = useRef(null);
  const [hoverPreviewItem, setHoverPreviewItem] = useState(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  useEffect(() => {
    if (!hoverPreviewItem) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow || "";
    };
  }, [hoverPreviewItem]);

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="fileCardGrid fileCardGrid--empty">
        <div className="emptyCell">
          {t("projectDetail.fileList.emptyMessage", {
            defaultValue: "Please upload UD files.",
          })}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fileCardGrid">
        {data.map((item) => {
          const {
            fileTypeKey,
            canOpenDetail,
            detailPath,
            previewSrc,
            videoSrc,
          } = getDetailMeta(project_id, item, detailSearch);
          const FileTypeIcon = fileTypeKey === "video" ? Film : ImageIcon;
          const isVideo = fileTypeKey === "video";

          return (
            <article
              className={`fileCardItem ${canOpenDetail ? "isClickable" : "isDisabled"} ${
                isVideo ? "isVideoCard" : ""
              }`}
              key={item?.id}
              onMouseEnter={() => {
                clearHoverTimer();
                hoverTimerRef.current = setTimeout(() => {
                  setHoverPreviewItem({
                    id: item?.id,
                    file_name: item?.file_name,
                    previewSrc: isVideo && videoSrc ? videoSrc : previewSrc,
                    previewType: isVideo && videoSrc ? "video" : "image",
                  });
                }, 1000);
              }}
              onMouseLeave={() => {
                clearHoverTimer();
                setHoverPreviewItem((current) =>
                  current?.id === item?.id ? null : current,
                );
              }}
            >
              <button
                className="fileCardItem__main"
                type="button"
                disabled={!canOpenDetail}
                onClick={() => {
                  if (canOpenDetail && detailPath) navigate(detailPath);
                }}
              >
                <span className="fileCardItem__typeIcon" aria-hidden="true">
                  <FileTypeIcon size={15} strokeWidth={2.1} />
                </span>
                <span className="fileCardItem__media">
                  <img
                    className="fileCardItem__image"
                    src={previewSrc}
                    alt={item?.file_name || "preview"}
                    loading="lazy"
                  />
                  {isVideo ? (
                    <>
                      <span className="fileCardItem__videoBadge">VIDEO</span>
                      <span
                        className="fileCardItem__playBadge"
                        aria-hidden="true"
                      >
                        <span />
                      </span>
                      <span
                        className="fileCardItem__filmFrame"
                        aria-hidden="true"
                      />
                    </>
                  ) : null}
                </span>
                <strong className="fileCardItem__name">
                  {item?.file_name}
                </strong>
              </button>
              <label className="fileCardItem__check">
                <input
                  checked={checkedItems.includes(item.id)}
                  onChange={() => handleToggleItem(item.id)}
                  onClick={(event) => event.stopPropagation()}
                  type="checkbox"
                />
              </label>
              <button
                className="fileCardItem__edit"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  clearHoverTimer();
                  onEditMetadata?.(item);
                }}
                title={t("projectDetail.fileList.column.previewAndEdit", {
                  defaultValue: "\uBBF8\uB9AC\uBCF4\uAE30 \uBC0F \uC218\uC815",
                })}
                aria-label={t("projectDetail.fileList.column.previewAndEdit", {
                  defaultValue: "\uBBF8\uB9AC\uBCF4\uAE30 \uBC0F \uC218\uC815",
                })}
              >
                <FilePenLine size={15} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </article>
          );
        })}
      </div>

      {hoverPreviewItem ? (
        <div className="fileCardHoverPreview" role="dialog" aria-modal="true">
          <div className="fileCardHoverPreview__inner">
            <p>{hoverPreviewItem.file_name}</p>
            {hoverPreviewItem.previewType === "video" ? (
              <video
                src={hoverPreviewItem.previewSrc}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <img
                src={hoverPreviewItem.previewSrc}
                alt={hoverPreviewItem.file_name}
              />
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

function FileMetadataEditModal({
  target,
  t,
  isSaving,
  onClose,
  onNavigate,
  onSubmit,
}) {
  const isBulk = target?.mode === "bulk";
  const [draft, setDraft] = useState(() => target?.initialValues ?? {});
  const [enabledFields, setEnabledFields] = useState(() =>
    Object.fromEntries(
      FILE_METADATA_EDIT_FIELDS.map((field) => [field.key, !isBulk]),
    ),
  );

  useEffect(() => {
    setDraft(target?.initialValues ?? {});
    setEnabledFields(
      Object.fromEntries(
        FILE_METADATA_EDIT_FIELDS.map((field) => [field.key, !isBulk]),
      ),
    );
  }, [isBulk, target]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSaving) {
        onClose();
        return;
      }

      const targetTagName = event.target?.tagName;
      const isEditingField = ["INPUT", "TEXTAREA", "SELECT"].includes(
        targetTagName,
      );
      if (isBulk || isSaving || isEditingField) return;

      if (event.key === "ArrowLeft" && target.slideIndex > 0) {
        event.preventDefault();
        onNavigate?.(-1);
      }
      if (
        event.key === "ArrowRight" &&
        target.slideIndex < target.slideCount - 1
      ) {
        event.preventDefault();
        onNavigate?.(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isBulk, isSaving, onClose, onNavigate, target]);

  const changedFields = useMemo(() => {
    return FILE_METADATA_EDIT_FIELDS.reduce((acc, field) => {
      const nextValue = normalizeMetadataValue(draft?.[field.key]);
      const previousValue = normalizeMetadataValue(
        target?.initialValues?.[field.key],
      );

      if (isBulk) {
        if (enabledFields[field.key]) {
          acc[field.key] = nextValue;
        }
        return acc;
      }

      if (nextValue !== previousValue) {
        acc[field.key] = nextValue;
      }
      return acc;
    }, {});
  }, [draft, enabledFields, isBulk, target]);

  const canSave = Object.keys(changedFields).length > 0 && !isSaving;
  const canFillEmpty =
    isBulk &&
    Object.values(changedFields).some(
      (value) => normalizeMetadataValue(value) !== "",
    ) &&
    !isSaving;
  const modalTitle = isBulk
    ? t("projectDetail.fileList.metadataEdit.bulkTitle", {
        defaultValue: "\uC77C\uAD04 \uC218\uC815",
      })
    : t("projectDetail.fileList.metadataEdit.singleTitle", {
        defaultValue: "파일 정보 수정",
      });
  const targetLabel = isBulk
    ? t("projectDetail.fileList.metadataEdit.bulkTarget", {
        defaultValue: "\uC120\uD0DD\uD55C {{count}}\uAC1C \uD30C\uC77C",
        count: target?.count ?? 0,
      })
    : target?.fileName ||
      t("projectDetail.fileList.metadataEdit.singleTarget", {
        defaultValue: "\uD30C\uC77C",
      });

  return (
    <div
      className="fileMetadataModalOverlay"
      role="presentation"
      onClick={() => {
        if (!isSaving) onClose();
      }}
    >
      <form
        className="fileMetadataModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fileMetadataModalTitle"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          if (canSave) {
            onSubmit(changedFields);
          }
        }}
      >
        <header className="fileMetadataModal__header">
          <div>
            <h3 id="fileMetadataModalTitle">{modalTitle}</h3>
            <p>{targetLabel}</p>
          </div>
          <button
            type="button"
            className="fileMetadataModal__close"
            onClick={onClose}
            disabled={isSaving}
            aria-label={t("projectDetail.fileList.metadataEdit.closeButton", {
              defaultValue: "\uB2EB\uAE30",
            })}
          >
            <X size={16} strokeWidth={2.2} aria-hidden="true" />
          </button>
        </header>

        <div className="fileMetadataModal__body">
          {!isBulk && target?.previewSrc ? (
            <div className="fileMetadataModal__previewSlider">
              <button
                type="button"
                className="fileMetadataModal__slideButton fileMetadataModal__slideButton--previous"
                onClick={() => onNavigate?.(-1)}
                disabled={isSaving || target.slideIndex <= 0}
                aria-label={t(
                  "projectDetail.fileList.metadataEdit.previousFile",
                  { defaultValue: "이전 파일" },
                )}
              >
                <ChevronLeft size={30} strokeWidth={2.5} aria-hidden="true" />
                <span>
                  {t("projectDetail.fileList.metadataEdit.previous", {
                    defaultValue: "이전",
                  })}
                </span>
              </button>
              <div className="fileMetadataModal__preview">
                <img
                  src={target.previewSrc}
                  alt={target.previewAlt || targetLabel}
                />
              </div>
              <button
                type="button"
                className="fileMetadataModal__slideButton fileMetadataModal__slideButton--next"
                onClick={() => onNavigate?.(1)}
                disabled={
                  isSaving || target.slideIndex >= target.slideCount - 1
                }
                aria-label={t("projectDetail.fileList.metadataEdit.nextFile", {
                  defaultValue: "다음 파일",
                })}
              >
                <ChevronRight size={30} strokeWidth={2.5} aria-hidden="true" />
                <span>
                  {t("projectDetail.fileList.metadataEdit.next", {
                    defaultValue: "다음",
                  })}
                </span>
              </button>
              <p className="fileMetadataModal__slidePosition">
                {target.slideIndex + 1} / {target.slideCount}
              </p>
            </div>
          ) : null}

          {FILE_METADATA_EDIT_FIELDS.map((field) => {
            const fieldEnabled = !isBulk || enabledFields[field.key];
            const label = getMetadataFieldLabel(field, t);

            return (
              <label
                className={`fileMetadataField ${
                  fieldEnabled ? "isEnabled" : ""
                }`}
                key={field.key}
              >
                <span className="fileMetadataField__toggleSlot">
                  {isBulk ? (
                    <input
                      type="checkbox"
                      checked={!!enabledFields[field.key]}
                      onChange={(event) =>
                        setEnabledFields((current) => ({
                          ...current,
                          [field.key]: event.target.checked,
                        }))
                      }
                      aria-label={t(
                        "projectDetail.fileList.metadataEdit.applyField",
                        {
                          defaultValue: "{{label}} \uC801\uC6A9",
                          label,
                        },
                      )}
                    />
                  ) : null}
                </span>
                <span className="fileMetadataField__label">{label}</span>
                <input
                  className="fileMetadataField__input"
                  maxLength={field.maxLength}
                  value={draft?.[field.key] ?? ""}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      [field.key]: event.target.value,
                    }))
                  }
                  disabled={!fieldEnabled || isSaving}
                />
              </label>
            );
          })}
        </div>

        <footer className="fileMetadataModal__footer">
          <button
            type="button"
            className="fileMetadataModal__cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            {t("projectDetail.fileList.metadataEdit.cancelButton", {
              defaultValue: "\uCDE8\uC18C",
            })}
          </button>
          {isBulk ? (
            <button
              type="button"
              className="fileMetadataModal__fillEmpty"
              disabled={!canFillEmpty}
              onClick={() =>
                onSubmit(changedFields, { applyMode: "emptyOnly" })
              }
            >
              {t("projectDetail.fileList.metadataEdit.fillEmptyButton", {
                defaultValue: "\uBE48\uCE78\uB9CC \uCC44\uC6B0\uAE30",
              })}
            </button>
          ) : null}
          <button
            type="submit"
            className="fileMetadataModal__save"
            disabled={!canSave}
          >
            {isSaving
              ? t("projectDetail.fileList.metadataEdit.savingButton", {
                  defaultValue: "\uC800\uC7A5 \uC911...",
                })
              : isBulk
                ? t("projectDetail.fileList.metadataEdit.applyAllButton", {
                    defaultValue: "\uC804\uCCB4 \uC801\uC6A9",
                  })
                : t("projectDetail.fileList.metadataEdit.saveButton", {
                    defaultValue: "\uC800\uC7A5",
                  })}
          </button>
        </footer>
      </form>
    </div>
  );
}
